import React, { useEffect, useState } from 'react';
import { useAgent } from './AgentProvider';

/**
 * Smart Agent Trigger System
 * 
 * This component provides intelligent, context-aware agent triggering
 * based on user behavior, page content, and business rules.
 */
export const AgentTriggerSystem = () => {
  const { spawnWidget, updateContext, currentContext } = useAgent();
  const [triggers, setTriggers] = useState([]);
  const [suppressedTriggers, setSuppressedTriggers] = useState(new Set());

  useEffect(() => {
    // Initialize trigger detection
    detectContextualTriggers();
    setupBehaviorTracking();
    setupTimerTriggers();

    return () => {
      // Cleanup event listeners
      cleanupBehaviorTracking();
    };
  }, []);

  useEffect(() => {
    // Re-evaluate triggers when context changes
    if (currentContext) {
      detectContextualTriggers();
    }
  }, [currentContext]);

  // Detect triggers based on current context
  const detectContextualTriggers = () => {
    const newTriggers = [];

    // Page-specific triggers
    newTriggers.push(...getPageBasedTriggers());
    
    // Data-based triggers
    newTriggers.push(...getDataBasedTriggers());
    
    // Time-based triggers
    newTriggers.push(...getTimeBasedTriggers());
    
    // Behavior-based triggers
    newTriggers.push(...getBehaviorBasedTriggers());

    // Filter out suppressed triggers
    const filteredTriggers = newTriggers.filter(
      trigger => !suppressedTriggers.has(trigger.id)
    );

    setTriggers(filteredTriggers);
    
    // Auto-execute high-priority triggers
    filteredTriggers
      .filter(trigger => trigger.priority === 'critical' && trigger.autoExecute)
      .forEach(trigger => executeTrigger(trigger));
  };

  // Page-based trigger detection
  const getPageBasedTriggers = () => {
    const triggers = [];
    const page = currentContext?.page || window.location.pathname;

    switch (page) {
      case '/succession/dashboard':
        triggers.push(...getDashboardTriggers());
        break;
      case '/succession/bcp':
        triggers.push(...getBcpTriggers());
        break;
      case '/succession/valuation':
        triggers.push(...getValuationTriggers());
        break;
      case '/succession/deals':
        triggers.push(...getDealTriggers());
        break;
    }

    return triggers;
  };

  const getDashboardTriggers = () => [
    {
      id: 'welcome_new_user',
      type: 'welcome',
      priority: 'high',
      condition: () => isNewUser(),
      action: {
        widgetType: 'succession_planner',
        props: {
          welcomeMode: true,
          initialMessage: "Welcome to FindBob! I'm here to help guide you through your succession planning journey."
        },
        position: 'center'
      },
      autoExecute: true,
      suppressAfter: 'first_execution'
    },
    {
      id: 'incomplete_planning_reminder',
      type: 'reminder',
      priority: 'medium',
      condition: () => hasIncompletePlanning(),
      action: {
        widgetType: 'succession_planner',
        props: {
          focusArea: 'planning_completion',
          contextualHelp: true
        },
        position: 'bottom-right'
      },
      delay: 30000, // 30 seconds after page load
      suppressAfter: 'daily'
    },
    {
      id: 'valuation_outdated',
      type: 'alert',
      priority: 'high',
      condition: () => isValuationOutdated(),
      action: {
        widgetType: 'valuation_advisor',
        props: {
          alertMode: true,
          message: "Your practice valuation is over 12 months old. Would you like to update it?"
        },
        position: 'modal'
      },
      autoExecute: false,
      suppressAfter: 'weekly'
    }
  ];

  const getBcpTriggers = () => [
    {
      id: 'bcp_form_help',
      type: 'assistance',
      priority: 'medium',
      condition: () => isStuckOnForm(),
      action: {
        widgetType: 'bcp_assistant',
        props: {
          formHelp: true,
          currentSection: getCurrentFormSection()
        },
        position: 'sidebar'
      },
      delay: 60000, // 1 minute of inactivity
      autoExecute: true,
      suppressAfter: 'session'
    },
    {
      id: 'bcp_completion_celebration',
      type: 'celebration',
      priority: 'low',
      condition: () => justCompletedBcp(),
      action: {
        widgetType: 'quick_actions',
        props: {
          celebrationMode: true,
          nextSteps: ['Review your BCP', 'Share with stakeholders', 'Start succession planning']
        },
        position: 'center'
      },
      autoExecute: true,
      suppressAfter: 'once'
    }
  ];

  const getValuationTriggers = () => [
    {
      id: 'valuation_insights',
      type: 'insights',
      priority: 'medium',
      condition: () => hasRecentValuation(),
      action: {
        widgetType: 'valuation_advisor',
        props: {
          analysisMode: true,
          showInsights: true
        },
        position: 'sidebar'
      },
      delay: 5000, // 5 seconds after page load
      autoExecute: true,
      suppressAfter: 'daily'
    }
  ];

  const getDealTriggers = () => [
    {
      id: 'new_buyer_interest',
      type: 'notification',
      priority: 'high',
      condition: () => hasNewBuyerActivity(),
      action: {
        widgetType: 'deal_helper',
        props: {
          newActivity: true,
          activityType: 'buyer_interest'
        },
        position: 'top-right'
      },
      autoExecute: true,
      suppressAfter: 'hourly'
    }
  ];

  // Data-based triggers
  const getDataBasedTriggers = () => {
    const triggers = [];

    // Check for missing critical data
    if (isMissingCriticalData()) {
      triggers.push({
        id: 'missing_data_prompt',
        type: 'data_collection',
        priority: 'high',
        condition: () => true,
        action: {
          widgetType: 'form_assistant',
          props: {
            missingData: getMissingDataFields(),
            priority: 'high'
          },
          position: 'modal'
        },
        autoExecute: false
      });
    }

    // Check for data inconsistencies
    if (hasDataInconsistencies()) {
      triggers.push({
        id: 'data_validation_alert',
        type: 'validation',
        priority: 'medium',
        condition: () => true,
        action: {
          widgetType: 'context_help',
          props: {
            validationIssues: getDataInconsistencies()
          },
          position: 'contextual'
        },
        autoExecute: true
      });
    }

    return triggers;
  };

  // Time-based triggers
  const getTimeBasedTriggers = () => {
    const triggers = [];
    const now = new Date();
    const hour = now.getHours();

    // End of day summary
    if (hour >= 17 && hour <= 18) {
      triggers.push({
        id: 'end_of_day_summary',
        type: 'summary',
        priority: 'low',
        condition: () => hasActivityToday(),
        action: {
          widgetType: 'quick_actions',
          props: {
            summaryMode: true,
            todaysActivity: getTodaysActivity()
          },
          position: 'bottom-right'
        },
        delay: 300000, // 5 minutes after 5 PM
        suppressAfter: 'daily'
      });
    }

    // Weekly planning reminder
    if (now.getDay() === 1 && hour >= 9 && hour <= 10) { // Monday morning
      triggers.push({
        id: 'weekly_planning_reminder',
        type: 'planning',
        priority: 'medium',
        condition: () => true,
        action: {
          widgetType: 'succession_planner',
          props: {
            weeklyPlanning: true
          },
          position: 'center'
        },
        suppressAfter: 'weekly'
      });
    }

    return triggers;
  };

  // Behavior-based triggers
  const getBehaviorBasedTriggers = () => {
    const triggers = [];

    // User seems confused (multiple back/forward, long idle times)
    if (detectConfusedBehavior()) {
      triggers.push({
        id: 'confusion_help',
        type: 'assistance',
        priority: 'high',
        condition: () => true,
        action: {
          widgetType: 'chat_interface',
          props: {
            helpMode: true,
            suggestion: "I noticed you might need some help. What can I assist you with?"
          },
          position: 'bottom-right'
        },
        autoExecute: true,
        suppressAfter: 'hourly'
      });
    }

    // User exploring specific topic repeatedly
    const focusArea = detectFocusArea();
    if (focusArea) {
      triggers.push({
        id: `focus_area_${focusArea}`,
        type: 'specialization',
        priority: 'medium',
        condition: () => true,
        action: {
          widgetType: getSpecializedWidget(focusArea),
          props: {
            focusArea: focusArea,
            deepDive: true
          },
          position: 'sidebar'
        },
        delay: 120000, // 2 minutes of focused behavior
        suppressAfter: 'session'
      });
    }

    return triggers;
  };

  // Behavior tracking setup
  const setupBehaviorTracking = () => {
    // Track mouse movement, clicks, scroll behavior, etc.
    document.addEventListener('mousemove', handleMouseMovement);
    document.addEventListener('click', handleClick);
    document.addEventListener('scroll', handleScroll);
    window.addEventListener('beforeunload', handlePageExit);
  };

  const cleanupBehaviorTracking = () => {
    document.removeEventListener('mousemove', handleMouseMovement);
    document.removeEventListener('click', handleClick);
    document.removeEventListener('scroll', handleScroll);
    window.removeEventListener('beforeunload', handlePageExit);
  };

  // Trigger execution
  const executeTrigger = async (trigger) => {
    try {
      // Log trigger execution for analytics
      console.log(`Executing trigger: ${trigger.id}`);
      
      // Execute the trigger action
      const widgetId = spawnWidget(
        trigger.action.widgetType,
        trigger.action.props,
        trigger.action.position
      );

      // Handle suppression
      if (trigger.suppressAfter) {
        handleTriggerSuppression(trigger);
      }

      // Track trigger execution
      trackTriggerExecution(trigger, widgetId);

    } catch (error) {
      console.error(`Failed to execute trigger ${trigger.id}:`, error);
    }
  };

  const handleTriggerSuppression = (trigger) => {
    const now = new Date();
    const suppressionKey = `${trigger.id}_${trigger.suppressAfter}`;
    
    switch (trigger.suppressAfter) {
      case 'once':
        setSuppressedTriggers(prev => new Set([...prev, trigger.id]));
        localStorage.setItem(`agent_trigger_suppressed_${trigger.id}`, 'true');
        break;
      case 'session':
        sessionStorage.setItem(`agent_trigger_suppressed_${trigger.id}`, now.toISOString());
        break;
      case 'daily':
        localStorage.setItem(`agent_trigger_suppressed_${trigger.id}`, now.toDateString());
        break;
      case 'weekly':
        const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
        localStorage.setItem(`agent_trigger_suppressed_${trigger.id}`, weekStart.toDateString());
        break;
      case 'hourly':
        localStorage.setItem(`agent_trigger_suppressed_${trigger.id}`, now.getTime() + 3600000); // 1 hour
        break;
    }
  };

  // Timer-based triggers
  const setupTimerTriggers = () => {
    triggers
      .filter(trigger => trigger.delay)
      .forEach(trigger => {
        setTimeout(() => {
          if (trigger.condition()) {
            executeTrigger(trigger);
          }
        }, trigger.delay);
      });
  };

  // Condition checking functions
  const isNewUser = () => {
    return !localStorage.getItem('findbob_returning_user');
  };

  const hasIncompletePlanning = () => {
    // Check if user has incomplete planning documents
    return currentContext?.planning_status && 
           Object.values(currentContext.planning_status).some(status => status !== 'completed');
  };

  const isValuationOutdated = () => {
    const valuation = currentContext?.valuation;
    if (!valuation?.created_at) return false;
    
    const ageMonths = (Date.now() - new Date(valuation.created_at)) / (1000 * 60 * 60 * 24 * 30);
    return ageMonths > 12;
  };

  const isStuckOnForm = () => {
    // Detect if user has been on form for extended time without progress
    return document.querySelector('form') && 
           performance.now() > 60000 && 
           !document.querySelector('form input:valid');
  };

  const justCompletedBcp = () => {
    return sessionStorage.getItem('bcp_just_completed') === 'true';
  };

  const hasRecentValuation = () => {
    const valuation = currentContext?.valuation;
    if (!valuation?.created_at) return false;
    
    const ageMonths = (Date.now() - new Date(valuation.created_at)) / (1000 * 60 * 60 * 24 * 30);
    return ageMonths <= 6; // Recent means within 6 months
  };

  const hasNewBuyerActivity = () => {
    // This would check for actual buyer activity from the backend
    return false; // Placeholder
  };

  const isMissingCriticalData = () => {
    // Check for missing essential practice information
    return !currentContext?.practice?.annual_revenue || 
           !currentContext?.practice?.location ||
           !currentContext?.practice?.specialty;
  };

  const hasDataInconsistencies = () => {
    // Check for data that doesn't make sense together
    return false; // Placeholder
  };

  const hasActivityToday = () => {
    return sessionStorage.getItem('daily_activity_count') > 0;
  };

  const detectConfusedBehavior = () => {
    // Analyze user behavior patterns for confusion indicators
    const backForwardCount = parseInt(sessionStorage.getItem('back_forward_count') || '0');
    const idleTime = parseInt(sessionStorage.getItem('current_idle_time') || '0');
    
    return backForwardCount > 5 || idleTime > 300000; // 5 minutes idle
  };

  const detectFocusArea = () => {
    // Analyze which area the user is spending most time on
    const pageViews = JSON.parse(sessionStorage.getItem('page_views') || '{}');
    const maxViews = Math.max(...Object.values(pageViews));
    const focusPage = Object.keys(pageViews).find(page => pageViews[page] === maxViews);
    
    if (focusPage && maxViews > 3) {
      if (focusPage.includes('valuation')) return 'valuation';
      if (focusPage.includes('bcp')) return 'bcp';
      if (focusPage.includes('deal')) return 'deals';
    }
    
    return null;
  };

  // Helper functions
  const getCurrentFormSection = () => {
    const activeSection = document.querySelector('.form-section.active');
    return activeSection?.getAttribute('data-section') || 'unknown';
  };

  const getMissingDataFields = () => {
    const missing = [];
    if (!currentContext?.practice?.annual_revenue) missing.push('Annual Revenue');
    if (!currentContext?.practice?.location) missing.push('Practice Location');
    if (!currentContext?.practice?.specialty) missing.push('Practice Specialty');
    return missing;
  };

  const getDataInconsistencies = () => {
    return []; // Placeholder
  };

  const getTodaysActivity = () => {
    return JSON.parse(sessionStorage.getItem('todays_activity') || '[]');
  };

  const getSpecializedWidget = (focusArea) => {
    switch (focusArea) {
      case 'valuation': return 'valuation_advisor';
      case 'bcp': return 'bcp_assistant';
      case 'deals': return 'deal_helper';
      default: return 'succession_planner';
    }
  };

  const trackTriggerExecution = (trigger, widgetId) => {
    // Track trigger execution for analytics
    const event = {
      trigger_id: trigger.id,
      trigger_type: trigger.type,
      widget_id: widgetId,
      timestamp: new Date().toISOString(),
      context: currentContext
    };
    
    console.log('Trigger executed:', event);
    
    // This would send to your analytics system
    // analytics.track('agent_trigger_executed', event);
  };

  // Event handlers for behavior tracking
  const handleMouseMovement = (event) => {
    // Track mouse movement patterns
    sessionStorage.setItem('last_mouse_activity', Date.now());
  };

  const handleClick = (event) => {
    // Track click patterns
    const clickCount = parseInt(sessionStorage.getItem('click_count') || '0') + 1;
    sessionStorage.setItem('click_count', clickCount);
  };

  const handleScroll = (event) => {
    // Track scroll behavior
    const scrollCount = parseInt(sessionStorage.getItem('scroll_count') || '0') + 1;
    sessionStorage.setItem('scroll_count', scrollCount);
  };

  const handlePageExit = (event) => {
    // Track page exit patterns
    const exitCount = parseInt(sessionStorage.getItem('exit_count') || '0') + 1;
    sessionStorage.setItem('exit_count', exitCount);
  };

  // This component doesn't render anything visible
  return null;
};

export default AgentTriggerSystem;